import { Button, Classes, Colors, Dialog, Icon } from "@blueprintjs/core";
import { FGCheckboxInput, FGCustomInput, FGCustomPanel, FGListen, FGSelectInput, useApiEffect } from "nsitools-react";
import * as React from "react";
import { FilePond } from "react-filepond";
import styled from "styled-components";
import * as Yup from "yup";
import { DrupalDogUpdate } from "./DrupalDogUpdate";

import { DogApi, EDogType, ERequestState, WebUserApi, WorkbookTypeDto } from "../../../../api";
import { AddButton } from "../../../../components";
import { useAuth, useGlobalData } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { TransientWorkbookList } from "../../../../pages/request/workbookRequest/item/detail/TransientWorkbookList";
import { DrupalStepperButtonBloc } from "../../../common";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";
import { DrupalWorkbookNewDogForm } from "./DrupalWorkbookNewDogForm";

const StyledInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 2px solid #3f76f9;
  background-color: #c0e4fc;
  margin: 0.5em 5em;
  padding: 0.5rem;
  text-align: center;
  border-radius: 10px;
`;

const StyledErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 2px solid #c84141;
  background-color: #ff867d;
  margin: 0.5em 5em;
  padding: 0.5rem;
  text-align: center;
  border-radius: 10px;
`;

const Container = styled.div``;

const StyledImageWarning = styled(Icon)`
  color: #fb3232;
  size: 9px;
  margin-right: 0.5rem;
`;

const StyledImage = styled(Icon)`
  color: #1e90ff;
  size: 9px;
  margin-right: 0.5rem;
`;

const ButtonContainer = styled.div`
  margin-left: 220px;
`;

export interface IDrupalWorkbookFormProps {
  showStepper?: boolean;
  workbookRequestId: number;
  stepNumber: number;
  initialdata: any;
  onStepChange: (newStep: number) => void;
  save: (data?: any) => Promise<any> | any;
  loading: boolean;
  files: any[];
  setFiles: React.Dispatch<React.SetStateAction<any[]>>;
}

export const DrupalWorkbookForm: React.FunctionComponent<IDrupalWorkbookFormProps> = ({
  showStepper = true,
  workbookRequestId,
  stepNumber,
  initialdata,
  onStepChange,
  save,
  loading,
  files,
  setFiles,
}) => {
  const [currentStep, setCurrentStep] = React.useState(stepNumber);
  const { user } = useAuth();
  const { t } = useTl();
  const [dogId, setDogId] = React.useState(0);
  const dogApi = useApiService(DogApi);
  const webUserApi = useApiService(WebUserApi);
  const { currentLanguage } = useGlobalData();
  const saveForm = React.useCallback(
    async (formikData) => {
      if (currentStep === 1) {
        formikData.dogId = dogId;
        await save(formikData);
      }
    },
    [currentStep, dogId, save]
  );
  const [visible, setvisible] = React.useState<boolean>();

  const [isBelgian, isBelgianLoading] = useApiEffect(() => webUserApi.isBelgian(user.userId), [user.userId]);
  const editMode = React.useMemo(
    () =>
      currentStep === 0 &&
      initialdata.request.status === ERequestState.REQUESTED &&
      !isBelgianLoading &&
      isBelgian?.value,
    [currentStep, initialdata.request.status, isBelgian?.value, isBelgianLoading]
  );
  const [dogs, dloading] = useReferential(
    (a) => a.getOwnedDogs(user.userId, initialdata?.dogId ? initialdata.dogId : dogId ?? 0, null, null, null),
    true,
    [initialdata, dogId]
  );
  const [workbookTypes, wtLoading] = useReferential(
    async (a) => (dogId ? a.getWorkbookTypes(currentLanguage.languageId, dogId) : []),
    false,
    [dogId]
  );

  const formSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("dog-deceased", t(ETLCodes.DogIsDeceased), async function (value) {
          if (!value) return true;
          if (workbookRequestId <= 0 || (initialdata?.request?.status === ERequestState.REQUESTED && !!value)) {
            const res = await dogApi.isDogDeceased(value);
            return !res.value;
          }
          return true;
        })
        .test("is-not-excluded", t(ETLCodes.DogIsExcluded), async function (value) {
          if (!value) return true;
          if (workbookRequestId <= 0 || initialdata?.request?.status === ERequestState.REQUESTED) {
            const result = await dogApi.isDogExcluded(value);
            return !result.value;
          }
          return true;
        })
        .test("dog-type", t(ETLCodes.DogIsNotBelgianOrImportOrForeignOrWithoutPedigree), async function (value) {
          if (!value) return true;
          const type = await dogApi.getDogType(value);
          return (
            type === EDogType.BelgianDogWithPedigree ||
            type === EDogType.ImportDog ||
            type === EDogType.ForeignDog ||
            type === EDogType.DogWithoutPedigree
          );
        }),
    });
  }, [dogApi, initialdata?.request?.status, t, workbookRequestId]);
  React.useEffect(() => {
    console.log(editMode);
  }, [editMode]);

  return (
    <>
      {!isBelgianLoading && !isBelgian?.value ? (
        <StyledErrorContainer>
          <div>
            <StyledImageWarning icon="warning-sign" />
            {t(ETLCodes.WorkbookCreationOnlyAllowedForBelgianResitents)}
          </div>
        </StyledErrorContainer>
      ) : null}
      <DrupalFormGenerator
        initialValues={initialdata}
        editMode={editMode}
        onSubmit={() => {}}
        saving={false}
        validationSchema={formSchema}
        loading={loading}
        hideButtons
        enableDirtyCheck={false}
      >
        <DrupalDogUpdate dogId={dogId} />
        <FGListen field="dogId" onChanged={(value) => setDogId(value)} />
        <FGSelectInput
          name="dogId"
          label={t(ETLCodes.Dog)}
          items={dogs}
          loading={dloading}
          readonly={initialdata.request.status !== ERequestState.REQUESTED}
        />
        <FGCustomInput>
          {({ formik }) =>
            !!initialdata?.workbookRequestId && initialdata?.request?.status !== ERequestState.REQUESTED ? (
              <>
                <TransientWorkbookList
                  dogId={dogId}
                  initialWorkbooks={formik?.values?.transientWorkbooksGridDtos || []}
                  onWorkbookChanged={() => {}}
                  editMode={false}
                  loading={loading}
                />
              </>
            ) : (
              <>
                {formik.values?.transientWorkbooksGridDtos?.map((c, index) => (
                  <FGSelectInput
                    key={index}
                    label={t(ETLCodes.Workbook)}
                    loading={wtLoading}
                    items={workbookTypes}
                    name={`transientWorkbooksGridDtos[${index}].workbookTypeId`}
                  />
                ))}
                {(!formik.values?.transientWorkbooksGridDtos ||
                  formik.values?.transientWorkbooksGridDtos?.length < 5) &&
                  currentStep === 0 && (
                    <ButtonContainer>
                      <AddButton
                        text={t(ETLCodes.AddNewWorkbook)}
                        onClick={() => {
                          const arr = formik.values?.transientWorkbooksGridDtos;
                          arr.push(WorkbookTypeDto.fromJS({ workbookTypeId: 0 }));
                          formik.setFieldValue("transientWorkbooksGridDtos", arr);
                        }}
                        secured={false}
                        disabled={!editMode}
                      />
                    </ButtonContainer>
                  )}
                {!dogId && (
                  <Container>
                    <FGCustomInput label=" ">
                      {(ctx) => (
                        <StyledInfoContainer>
                          <div>
                            <StyledImage icon="info-sign" />
                            {t(ETLCodes.DogNotFoundClickAdd)}
                          </div>
                        </StyledInfoContainer>
                      )}
                    </FGCustomInput>
                    <FGCustomInput label=" ">
                      {(ctx) => (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Button
                            style={{
                              width: "15%",
                            }}
                            intent="primary"
                            minimal={true}
                            icon="add"
                            text={t(ETLCodes.DrupalCreateNewDog)}
                            onClick={() => setvisible(!visible)}
                            disabled={!editMode}
                          />
                        </div>
                      )}
                    </FGCustomInput>
                  </Container>
                )}
              </>
            )
          }
        </FGCustomInput>
        <FGCheckboxInput
          name="registeredLetter"
          label={t(ETLCodes.IsRegisteredLetter)}
          readonly={initialdata?.request?.status !== ERequestState.REQUESTED}
          helperText={t(ETLCodes.IfNotRegisteredLetterSrshIsNotResponsibleForAnyLostDocument)}
        />
        {currentStep === 0 && initialdata.request.status === ERequestState.REQUESTED ? (
          <FGCustomInput label={t(ETLCodes.Documents)}>
            {(ctx) => (
              <FilePond
                files={files}
                allowMultiple={true}
                onupdatefiles={(f) => {
                  ctx.formik.setFieldValue("files", f);
                  setFiles(f);
                }}
                labelIdle={t(ETLCodes.FilePondDragOrSelect)}
                disabled={currentStep !== 0}
              />
            )}
          </FGCustomInput>
        ) : null}
        {showStepper ? (
          <FGCustomPanel>
            {(ctx) => {
              return (
                <DrupalStepperButtonBloc
                  backOnly={initialdata?.request?.status !== ERequestState.REQUESTED}
                  disabled={
                    ctx.formik.values === initialdata ||
                    ctx.formik.values["transientWorkbooksGridDtos"].length < 1 ||
                    ctx.formik.values["transientWorkbooksGridDtos"].find(
                      (c) => c.workbookTypeId === 0 || c.workbookTypeId === null
                    )
                  }
                  loading={loading}
                  onStepChange={(newStep) => {
                    setCurrentStep(newStep);
                    onStepChange(newStep);
                  }}
                  stepNumber={currentStep}
                  onNextFn={() => saveForm(ctx.formik.values)}
                  formik={ctx.formik}
                />
              );
            }}
          </FGCustomPanel>
        ) : null}
      </DrupalFormGenerator>
      <Dialog
        title={t(ETLCodes.NewDog)}
        isOpen={visible}
        onClose={() => setvisible(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        className={Classes.DIALOG}
        style={{ width: 1200, background: Colors.WHITE }}
      >
        <div style={{ margin: "1rem" }}>
          <DrupalWorkbookNewDogForm setVisible={setvisible} setDogId={setDogId} />
        </div>
      </Dialog>
    </>
  );
};
